import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  Shield,
  Key,
  Lock,
  Smartphone,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertCircle,
  Copy,
  QrCode,
  ShieldCheck,
  ShieldOff,
  Settings,
} from 'lucide-react';
import { setup2fa, enable2fa, disable2fa, changePassword } from '../../api/auth.api';
import toast from 'react-hot-toast';
import { PageFadeSection } from '../../components/common/PageFadeSection';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // ── Change Password state ──
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ── 2FA state ──
  const [twoFaEnabled, setTwoFaEnabled] = useState(user?.twoFactorEnabled || false);
  const [setupData, setSetupData] = useState(null); // { sharedKey, qrCodeUri }
  const [setupStep, setSetupStep] = useState('idle'); // idle | setup | verify
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  // ── Change Password ──
  const handleChangePassword = useCallback(async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error(t('pages.settings.toastPwMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('pages.settings.toastPwMatch'));
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success(t('pages.settings.toastPwOk'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || t('pages.settings.toastPwFail'));
    } finally {
      setPasswordLoading(false);
    }
  }, [currentPassword, newPassword, confirmPassword, t]);

  // ── 2FA Setup ──
  const handleSetup2fa = useCallback(async () => {
    setTwoFaLoading(true);
    try {
      const data = await setup2fa();
      setSetupData(data);
      setSetupStep('setup');
    } catch (error) {
      toast.error(error.response?.data?.message || t('pages.settings.toast2faSetupFail'));
    } finally {
      setTwoFaLoading(false);
    }
  }, [t]);

  const handleVerify2fa = useCallback(async () => {
    if (verifyCode.length !== 6) {
      toast.error(t('pages.settings.toastCode6'));
      return;
    }
    setTwoFaLoading(true);
    try {
      await enable2fa(verifyCode);
      setTwoFaEnabled(true);
      setSetupStep('idle');
      setSetupData(null);
      setVerifyCode('');
      toast.success(t('pages.settings.toast2faOk'));
      // Update user in localStorage
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.twoFactorEnabled = true;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch (error) {
      toast.error(error.response?.data?.message || t('pages.settings.toast2faInvalid'));
    } finally {
      setTwoFaLoading(false);
    }
  }, [verifyCode, t]);

  const handleDisable2fa = useCallback(async () => {
    if (disableCode.length !== 6) {
      toast.error(t('pages.settings.toastCode6'));
      return;
    }
    setTwoFaLoading(true);
    try {
      await disable2fa(disableCode);
      setTwoFaEnabled(false);
      setShowDisableConfirm(false);
      setDisableCode('');
      toast.success(t('pages.settings.toast2faDisabled'));
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.twoFactorEnabled = false;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch (error) {
      toast.error(error.response?.data?.message || t('pages.settings.toast2faInvalidDisable'));
    } finally {
      setTwoFaLoading(false);
    }
  }, [disableCode, t]);

  const copyKey = useCallback(() => {
    if (setupData?.sharedKey) {
      navigator.clipboard.writeText(setupData.sharedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  }, [setupData]);

  const cancelSetup = () => {
    setSetupStep('idle');
    setSetupData(null);
    setVerifyCode('');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-3xl space-y-6 animate-fade-in">

        <PageFadeSection>
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg shadow-indigo-500/20">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('pages.settings.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.settings.subtitle')}</p>
          </div>
        </div>

        </PageFadeSection>
        <PageFadeSection>
        {/* ═══════════ Change Password ═══════════ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.settings.changePassword')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.settings.changePasswordSub')}</p>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.currentPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
                <input
                  type={showCurrentPw ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  placeholder={t('pages.settings.placeholderCurrent')}
                  required
                />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.newPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
                <input
                  type={showNewPw ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  placeholder={t('pages.settings.placeholderNew')}
                  minLength={8}
                  required
                />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* Confirm */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.confirmNewPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  placeholder={t('pages.settings.placeholderConfirm')}
                  required
                />
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {t('pages.settings.passwordsMismatch')}
                </p>
              )}
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={passwordLoading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                {passwordLoading ? t('pages.settings.changing') : t('pages.settings.changePasswordBtn')}
              </button>
            </div>
          </form>
        </div>

        {/* ═══════════ Two-Factor Authentication ═══════════ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${twoFaEnabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                <Shield className={`w-5 h-5 ${twoFaEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.settings.twoFactorTitle')}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.settings.twoFactorSub')}</p>
              </div>
            </div>
            {/* Status badge */}
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${twoFaEnabled
              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
              {twoFaEnabled ? t('pages.settings.enabled') : t('pages.settings.disabled')}
            </span>
          </div>

          <div className="p-6">
            {/* ── 2FA is OFF and idle ── */}
            {!twoFaEnabled && setupStep === 'idle' && (
              <div className="text-center py-4">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center mb-4">
                  <Smartphone className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('pages.settings.protectTitle')}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                  {t('pages.settings.protectBody')}
                </p>
                <button
                  onClick={handleSetup2fa}
                  disabled={twoFaLoading}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50"
                >
                  {twoFaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {t('pages.settings.enable2fa')}
                </button>
              </div>
            )}

            {/* ── Setup step: show QR code + secret ── */}
            {!twoFaEnabled && setupStep === 'setup' && setupData && (
              <div className="space-y-6">
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>{t('pages.settings.step1')}</strong> {t('pages.settings.step1Body')}
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex flex-col items-center">
                  <div className="p-4 bg-white rounded-2xl shadow-inner border border-gray-200 dark:border-gray-600 mb-4">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qrCodeUri)}`}
                      alt={t('pages.settings.qrAlt')}
                      className="w-48 h-48"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('pages.settings.scanHint')}</p>
                </div>

                {/* Manual key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.manualKey')}</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 rounded-xl text-sm font-mono text-gray-900 dark:text-white tracking-wider break-all">
                      {setupData.sharedKey}
                    </code>
                    <button onClick={copyKey} className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" title={t('pages.settings.copyKeyTitle')}>
                      {keyCopied ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                    </button>
                  </div>
                </div>

                {/* Verify code */}
                <div>
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 mb-4">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-700 dark:text-amber-300">
                      <strong>{t('pages.settings.step2')}</strong> {t('pages.settings.step2Body')}
                    </div>
                  </div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.verificationCode')}</label>
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl font-mono tracking-[0.5em] focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleVerify2fa}
                    disabled={twoFaLoading || verifyCode.length !== 6}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold transition-all shadow-md disabled:opacity-50"
                  >
                    {twoFaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {t('pages.settings.verifyEnable')}
                  </button>
                  <button
                    onClick={cancelSetup}
                    className="px-5 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    {t('commonUi.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* ── 2FA is ON ── */}
            {twoFaEnabled && !showDisableConfirm && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{t('pages.settings.twoFaActive')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.settings.twoFaActiveSub')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDisableConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl font-medium text-sm transition-all"
                >
                  <ShieldOff className="w-4 h-4" />
                  {t('pages.settings.disable')}
                </button>
              </div>
            )}

            {/* ── Disable 2FA confirm ── */}
            {twoFaEnabled && showDisableConfirm && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700 dark:text-red-300">
                    <strong>{t('pages.settings.warnDisable')}</strong> {t('pages.settings.warnDisableBody')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('pages.settings.authCode')}</label>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl font-mono tracking-[0.5em] focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDisable2fa}
                    disabled={twoFaLoading || disableCode.length !== 6}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-md disabled:opacity-50"
                  >
                    {twoFaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                    {t('pages.settings.confirmDisable')}
                  </button>
                  <button
                    onClick={() => { setShowDisableConfirm(false); setDisableCode(''); }}
                    className="px-5 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    {t('commonUi.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════ Account Info (read-only) ═══════════ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.settings.accountInfo')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.settings.accountInfoSub')}</p>
            </div>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoItem label={t('pages.settings.name')} value={user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()} />
            <InfoItem label={t('pages.settings.email')} value={user?.email} />
            <InfoItem label={t('pages.settings.department')} value={user?.department || '—'} />
            <InfoItem label={t('pages.settings.sapEmployeeId')} value={user?.sapEmployeeId || t('pages.settings.notLinked')} />
          </div>
        </div>
        </PageFadeSection>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-white">{value || '—'}</p>
  </div>
);

export default SettingsPage;
